import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { StyledErrorText } from '@shared/components/errorText';
import CustomFormLabel from '@shared/components/forms/theme-elements/CustomFormLabel';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { IconTrash } from '@tabler/icons-react';
import React from 'react';

interface HeaderItemLike {
  key: string;
  value: string;
}

interface KeyValueHeadersEditorProps<THeader extends HeaderItemLike = HeaderItemLike> {
  label: string;
  addLabel: string;
  keyLabel: string;
  valueLabel: string;
  emptyLabel: string;
  keyPlaceholder: string;
  valuePlaceholder: string;
  items: THeader[];
  error?: string;
  disabled?: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: 'key' | 'value', value: string) => void;
}

const KeyValueHeadersEditor = <THeader extends HeaderItemLike = HeaderItemLike>({
  label,
  addLabel,
  keyLabel,
  valueLabel,
  emptyLabel,
  keyPlaceholder,
  valuePlaceholder,
  items,
  error,
  disabled = false,
  onAdd,
  onRemove,
  onChange,
}: KeyValueHeadersEditorProps<THeader>) => {
  const theme = useTheme();

  return (
    <Box>
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}>
        <CustomFormLabel sx={{ mb: 0, mt: 0, fontWeight: 500 }}>{label}</CustomFormLabel>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onAdd}
          disabled={disabled}
          sx={{ textTransform: 'none' }}>
          {addLabel}
        </Button>
      </Box>

      {items.length > 0 ? (
        <BorderedGrid sx={{ padding: '12px', marginBottom: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '35%' }}>{keyLabel}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{valueLabel}</TableCell>
                  <TableCell sx={{ width: '50px' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ borderBottom: 'none', padding: '8px 8px 8px 0' }}>
                      <CustomTextField
                        size="small"
                        value={item.key}
                        placeholder={keyPlaceholder}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(index, 'key', event.target.value)
                        }
                        fullWidth
                        disabled={disabled}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', padding: '8px' }}>
                      <CustomTextField
                        size="small"
                        value={item.value}
                        placeholder={valuePlaceholder}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(index, 'value', event.target.value)
                        }
                        fullWidth
                        disabled={disabled}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', padding: '8px 0 8px 8px' }}>
                      <IconButton
                        size="small"
                        onClick={() => onRemove(index)}
                        disabled={disabled}
                        sx={{ color: theme.palette.error.main }}>
                        <IconTrash size={16} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {error ? <StyledErrorText sx={{ mt: 1 }}>{error}</StyledErrorText> : null}
        </BorderedGrid>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {emptyLabel}
        </Typography>
      )}
    </Box>
  );
};

export default KeyValueHeadersEditor;
